import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Icon } from '@codex-brightworld/icons';

const StyledUlList = styled.ul`
  margin: 0;
  padding: 0;
  display: flex;
  border-radius: 8px;
`;

const StyledPageNumber = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.36;
 
  ${({ theme }) => css`
    color: ${theme.colors.primary.purple};
    background-color: ${theme.colors.neutral.white};
  `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.neutral.grey[0]};
    color: ${({ theme }) => theme.colors.primary.purpleHover}
  }
`;

const StyledListItem = styled.li`
  display: flex;
  justify-content: center;
  align-items: stretch;
  border: 1px solid;
  border-left: none;
  width: 40px;
  height: 40px;
  
  &:last-child, :last-child ${StyledPageNumber} {
    border-radius: 0 8px 8px 0;
  }
  
  &:first-child {
    border-left: 1px solid;
    margin-left: -1px;
  }

  &:first-child, :first-child ${StyledPageNumber} {
    border-radius: 8px 0 0 8px;
  }

  ${({ theme }) => css`
   border-color: ${theme.colors.neutral.grey[2]};

    &:first-child {
     border-left-color: ${theme.colors.neutral.grey[2]};
    }
  `}
`;


const StyledDots = styled(StyledPageNumber)`
   &:hover {
    background-color: ${({ theme }) => theme.colors.neutral.white};
   }
`;

const StyledCurrentPageNumber = styled(StyledPageNumber)`
  
  ${({ theme }) => css`
    background-color: ${theme.colors.neutral.grey[0]};
    color: ${theme.colors.primary.purpleHover};
  `}
`;

const PaginatorSM = (props) => {
  const onLinkClick = (e, page) => {
    e.preventDefault();
    props.onLinkClick(page.url, page.label);
  };

  const getCurrentPageIndex = () => props.pagesList.findIndex(page => !!page.isCurrent);

  const createAnchor = (page) => {
    const {
      url, anchorLabel, isCurrent, title
    } = page;
    if (isCurrent) {
      return (
        <StyledCurrentPageNumber title={title}>
          {anchorLabel}
        </StyledCurrentPageNumber>
      );
    }
    return (
      <StyledPageNumber
        onClick={e => onLinkClick(e, page)}
        href={url}
        title={title}
      >
        {anchorLabel}
      </StyledPageNumber>
    );
  };

  const {
    renderArrowLeft,
    renderArrowRight,
    renderPagesList,
    pagesList,
    leftArrow,
    rightArrow,
    dots,
    renderPageLink,
    siblingCount
  } = props;

  return (
    <StyledUlList data-qa={props.qa}>
      {renderArrowLeft(leftArrow, pagesList, getCurrentPageIndex, createAnchor)}
      {renderPagesList(dots, pagesList, siblingCount, getCurrentPageIndex, renderPageLink, createAnchor)}
      {renderArrowRight(rightArrow, pagesList, getCurrentPageIndex, createAnchor)}
    </StyledUlList>
  );
};

PaginatorSM.displayName = 'PaginatorSM';

PaginatorSM.propTypes = {
  pagesList: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    label: PropTypes.number,
    isCurrent: PropTypes.bool
  })),
  onLinkClick: PropTypes.func,
  /**
   * @param {string} arrow
   * @param {array} pagesList
   * @param {function} getCurrentPageIndex
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderArrowLeft: PropTypes.func,
  /**
   * @param {string} arrow
   * @param {array} pagesList
   * @param {function} getCurrentPageIndex
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderArrowRight: PropTypes.func,
  /**
   * @param {object} page
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderPageLink: PropTypes.func,
  /**
   * @param {string} dots
   * @param {array} pagesList
   * @param {number} siblingCount
   * @param {function} getCurrentPageIndex
   * @param {function} createAnchor
   * @return {React.Node}
   */
  renderPagesList: PropTypes.func,
  siblingCount: PropTypes.number,
  leftArrow: PropTypes.node,
  rightArrow: PropTypes.node,
  dots: PropTypes.node,
  qa: PropTypes.string
};

PaginatorSM.defaultProps = {
  pagesList: [],
  onLinkClick: () => {},
  renderArrowLeft: (arrow, pagesList, getCurrentPageIndex, createAnchor) => {
    const previousPage = pagesList[getCurrentPageIndex() - 1];

    if (previousPage) {
      return (
        <StyledListItem key="previous">
          {createAnchor({ ...previousPage, anchorLabel: arrow, title: 'previous' })}
        </StyledListItem>
      );
    }
    return null;
  },
  renderArrowRight: (arrow, pagesList, getCurrentPageIndex, createAnchor) => {
    const nextPage = pagesList[getCurrentPageIndex() + 1];

    if (nextPage) {
      return (
        <StyledListItem key="next">
          {createAnchor({ ...nextPage, anchorLabel: arrow, title: 'next' })}
        </StyledListItem>
      );
    }
    return null;
  },
  renderPageLink: (page, createAnchor) => (
    <StyledListItem key={page.label}>
      {createAnchor({ ...page, anchorLabel: page.label, title: page.label })}
    </StyledListItem>
  ),
  siblingCount: 2,
  renderPagesList: (dots, pagesList, siblingCount, getCurrentPageIndex, renderPageLink, createAnchor) => {
        
    const omittedNumbersLeft = getCurrentPageIndex() - siblingCount - 1;
    const omittedNumbersRight = pagesList.length - getCurrentPageIndex() - siblingCount - 2;
    
    const middleRangeStart = getCurrentPageIndex() - siblingCount > 0 && omittedNumbersLeft > 1
      ? getCurrentPageIndex() - siblingCount : 1;

    const middleRangeEnd = getCurrentPageIndex() + siblingCount < pagesList.length - 1 && omittedNumbersRight > 1
      ? getCurrentPageIndex() + siblingCount + 1 : pagesList.length - 1;
    
    const middleRange = pagesList.slice(middleRangeStart, middleRangeEnd);

    if (pagesList.length <= 6) {
      return pagesList.map(item => renderPageLink(item, createAnchor));
    }
    
    return (
      <>
        {renderPageLink(pagesList[0], createAnchor)}
        {omittedNumbersLeft > 1 && (<StyledListItem><StyledDots>{dots}</StyledDots></StyledListItem>)}
        {middleRange.map(item => renderPageLink(item, createAnchor))}
        {omittedNumbersRight > 1 && (<StyledListItem><StyledDots>{dots}</StyledDots></StyledListItem>)}
        {renderPageLink(pagesList[pagesList.length - 1], createAnchor)}
      </>
    );
  },
  leftArrow: <Icon icon="arrowright" rotate={180} />,
  rightArrow: <Icon icon="arrowright" />,
  dots: <Icon icon="details" />,
  qa: undefined
};

export default PaginatorSM;
